import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { UserInfo } from '../types/common';
import { logoutApi } from '@/api/modules/auth/logout';

/**
 * 用户状态接口
 */
export interface UserState {
  userInfo: UserInfo | null;
  token: string | null;
  setUserInfo: (info: UserInfo | ((prev: UserInfo | null) => Partial<UserInfo>)) => void;
  setToken: (token: string) => void;
  clearUser: () => void;
}

/**
 * 用户状态管理 Store
 * 使用 Zustand 创建轻量级状态管理
 * 支持状态持久化到 localStorage
 */
export const useUserStore = create<UserState>()(
  persist(
    (set) => ({
      // 初始状态
      userInfo: {
        userId: '',
        phone: '',
        username: '',
        email: '',
        avatarUrl: '',
        clientId: '',
        permissionCodes: [],
      },
      token: '',
      // 设置用户信息
      setUserInfo: (info: UserInfo | ((prev: UserInfo | null) => Partial<UserInfo>)) => {
        set((state) => ({
          userInfo:
            typeof info === 'function'
              ? ({
                  ...state.userInfo,
                  ...info(state.userInfo),
                } as UserInfo)
              : info,
        }));
      },

      // 设置 token
      setToken: (token: string) => {
        set({ token });
      },

      // 清除用户信息（用于退出登录）
      clearUser: () => {
        set({ userInfo: null, token: null });
      },
    }),
    {
      name: 'user-storage', // localStorage 中的 key
      version: 2,
      // 可选：自定义存储方式
      // storage: createJSONStorage(() => sessionStorage),
    }
  )
);

export const getUserBaseInfo = () => useUserStore.getState().userInfo;

export const getAuthToken = () => useUserStore.getState().token;

export const getUserRoleInfo = () => useUserStore.getState().userInfo?.roleInfo;

export const ADMIN_ROLE_KEYS = ['ROLE_COMPANY_ADMIN', 'ROLE_COMPANY_SUPER_ADMIN'];

export const isAdminRole = () => {
  return useUserStore
    .getState()
    .userInfo?.roleInfo?.roleKeys.some((roleKey) => ADMIN_ROLE_KEYS.includes(roleKey));
};

let intentionalLogoutPending = false;

/** 主动退出时，避免路由守卫把清理登录态误判为未登录访问 */
export const isIntentionalLogoutPending = () => intentionalLogoutPending;

/** 到达登录页后结束主动退出的跳转状态 */
export const finishIntentionalLogout = () => {
  intentionalLogoutPending = false;
};

/**
 * 清理当前用户的鉴权信息和临时上传凭证
 */
export const clearSession = () => {
  useUserStore.getState().clearUser();
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem('sts-storage');
  }
  /** 动态加载避免用户 Store 与请求模块形成静态循环依赖，同时清理当前页面内存中的旧凭证 */
  void import('./stsStore').then(({ clearStsToken }) => {
    clearStsToken();
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem('sts-storage');
    }
  });
};

/**
 * 主动退出时先通知服务端，再清理本地会话；请求失败也完成本地退出
 */
export const logout = async () => {
  intentionalLogoutPending = true;
  try {
    await logoutApi();
    return true;
  } catch (error) {
    console.warn('登出请求失败，继续清理本地会话', error);
    return false;
  } finally {
    clearSession();
  }
};
